import React, { useRef } from 'react';
import { FiX, FiPaperclip } from 'react-icons/fi';

const MAX_FILES = 10;
const inputBaseClass =
  'w-full px-4 py-3 bg-white/10 border-2 border-dashed border-white/30 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500';

/**
 * Multi file input: shows chosen files in a list with remove per file.
 * value: File[] (controlled), onChange: (files: File[]) => void.
 */
export default function MultiFileInput({
  label,
  name,
  value = [],
  onChange,
  accept = 'image/*,.pdf',
  maxFiles = MAX_FILES,
  required,
  hint,
  className = '',
}) {
  const inputRef = useRef(null);
  const files = Array.isArray(value) ? value : [];

  const handleInputChange = (e) => {
    const added = e.target.files ? Array.from(e.target.files) : [];
    const merged = [...files, ...added].slice(0, maxFiles);
    onChange(merged);
    e.target.value = '';
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = (index) => {
    const next = files.filter((_, i) => i !== index);
    onChange(next);
  };

  const canAdd = files.length < maxFiles;

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-semibold text-white mb-2">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
      )}
      {canAdd && (
        <div className="relative">
          <input
            ref={inputRef}
            type="file"
            name={name}
            multiple
            accept={accept}
            onChange={handleInputChange}
            className={inputBaseClass}
            aria-label={label}
          />
          <FiPaperclip className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" />
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-3 space-y-2" role="list">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}-${file.size}`}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
            >
              <span className="truncate flex-1 min-w-0" title={file.name}>
                {file.name}
              </span>
              <span className="text-white/50 text-xs shrink-0">
                {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-red-500/30 transition-colors shrink-0"
                aria-label={`Remove ${file.name}`}
              >
                <FiX className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {hint && (
        <p className="text-xs text-white/60 mt-1.5">
          {hint} {files.length > 0 && `${files.length} of ${maxFiles} selected.`}
          {files.length >= maxFiles && ' Remove one to add another.'}
        </p>
      )}
      {!hint && files.length > 0 && (
        <p className="text-xs text-white/60 mt-1.5">
          {files.length} of {maxFiles} selected.
          {files.length >= maxFiles && ' Remove one to add another.'}
        </p>
      )}
    </div>
  );
}
